
import { Component } from "vue-property-decorator";
import BaseAuthenticatedView from "@/views/BaseAuthenticatedView.vue";
import AllBotsChartWrapper from "./components/charts/overview/AllBotsChartWrapper.vue";
import BotsActivityTable from "./components/BotsActivityTable.vue";
import TotalBotsStats from "@/models/bots/TotalBotStats";
import TradingBotsWrapper from "./components/trading-bot-chips/TradingBotsWrapper.vue";
import { PercentIndicator } from "@/mixins/percentIndicatorMixin";
import Formatter from "@/mixins/formatterMixin";
import BalanceInUseDonutChart from "./components/charts/BalanceInUseDonutChart.vue";
import TooltipIcon from "@/components/general/TooltipIcon.vue";
import TradeModel from "@/models/Trade";
import OpenOrder from "@/models/OpenOrder";
import Coin, { mapToBalanceInUsePerCoinType } from "@/models/Coin";
import SubscriptionPlan from "@/models/subscription/SubscriptionPlan";
import SensitiveNumbersMixin from "@/mixins/sensitiveNumbersMixin";
import HideShowOptionView from "@/components/general/HideShowOptionView.vue";
import FunctionalityLimitationMixin from "@/mixins/functionalityLimitationMixin";
import TimeFrameSelector from "./components/trading-bot-chips/TimeFrameSelector.vue";

@Component({
  components: {
    AllBotsChartWrapper,
    BotsActivityTable,
    TradingBotsWrapper,
    BalanceInUseDonutChart,
    TooltipIcon,
    HideShowOptionView,
    TimeFrameSelector,
  },
  mixins: [
    PercentIndicator,
    Formatter,
    SensitiveNumbersMixin,
    FunctionalityLimitationMixin,
  ],
})
export default class Dashboard extends BaseAuthenticatedView {
  // 🗻 Table headers displayed in the "Open Orders" tab for all bots
  private openOrdersHeadersAllBots = [
    { text: "Created At", value: "createTime" },
    { text: "Expires In", value: "expireTime" },
    { text: "Market", value: "market" },
    { text: "Side", value: "side" },
    { text: "Type", value: "type" },
    { text: "Amount", value: "volume" },
    { text: "Price", value: "price" },
    { text: "Bot", value: "botName" },
  ];

  // 🗻 Table headers displayed in the "Trade History" tab for all bots
  private tradeHistoryHeadersAllBots = [
    { text: "Date Executed", value: "date" },
    { text: "Market", value: "market" },
    { text: "Side", value: "type" },
    { text: "Price", value: "price" },
    { text: "Amount", value: "amount" },
    { text: "Exchange Fee", value: "fee" },
    { text: "Crypto/Stablecoin Ratio", value: "pRatio" },
    { text: "OBT Fee", value: "obt_fee" },
    { text: "Ending Balance", value: "balance" },
    { text: "Bot", value: "botName" },
  ];

  // 🚩 Flag for whether the onboarding dialog should be shown
  private showOnboardingDialog: boolean = false;

  // 📦 Computed property that aims to return the total trade history for all bots
  private tradeHistory: TradeModel[] = [];

  // 📦 The stats of all bots combined
  private get totalBotsStats(): TotalBotsStats | undefined {
    return this.$store.state.bots.totalBotsStats;
  }

  // 📦 Computed property that aims to return the total open orders for all bots
  private get totalOpenOrders(): OpenOrder[] {
    return this.totalBotsStats ? this.totalBotsStats.totalOpenOrders : [];
  }

  // 📦 Computed property that aims to return the balance in user per for all bots
  private get balanceInUsePerCoin(): Coin[] {
    return this.totalBotsStats
      ? !this.$store.state.userPreferences.showDetailedDashboardDonutChart
        ? mapToBalanceInUsePerCoinType(this.totalBotsStats.balanceInUsePerCoin)
        : this.totalBotsStats.balanceInUsePerCoin
      : [];
  }

  // 📦 The current subscription plan the user is on
  private get currentSubscriptionPlan(): SubscriptionPlan {
    return this.$store.state.subscription.currentSubscriptionPlan;
  }

  protected async created() {
    await super.mounted();
  }

  protected async mounted() {
    await super.mounted();

    this.$store.dispatch("changeAppBarLoading", true);
    this.$store.dispatch("fetchWalletBalance");

    await Promise.all([
      this.$store.dispatch("fetchTradeHistory"),
      this.$store.dispatch("fetchTradingBotStats"),
      this.$store.dispatch("getCurrentSubscription"),
      this.$store.dispatch("fetchCurrentSubscriptionPlan"),
    ]).then((bulkResponse: any[]) => {
      if (bulkResponse.length > 0) {
        this.tradeHistory = bulkResponse[0];
      }
    });

    this.$store.dispatch("changeAppBarLoading", false);
  }
}
